import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Button from '../../../components/Button/CustomButton';
import Typography from '../../../components/Typography/CustomTypography';
import SnackBar from '../../../components/Snackbar/Snackbar';
import CleanableInput from '../../../components/Input/CleanableInput';
import Validation from '../../../common/Validation';
import Loader from '../../../components/Loader/Loader';
import UploadButton from '../../../components/Button/UploadButton';
import { infoBoxShadow, infoColor } from '../../../assets/jss/global';
import AlertDialog from '../../../components/Dialog/AlertDialog';

const useStyles = makeStyles({
    main: {
        padding: '23px',
    },
    fieldset: {
        borderColor: infoColor,
        borderRadius: '8px',
        ...infoBoxShadow,
        padding: '26px',
    },
    button: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '23px',
    },
    marginBottom: {
        display: 'flex',

        '&:not(:last-child)': {
            marginBottom: '15px',
        },
    },
});

const OuterServerMail = (props) => {
    const {
        getOuterServerMail,
        clearGetOuterServerMail,
        updateOuterServerMail,
        clearUpdateOuterServerMail,
        isLoading,
        mailSetting,
        updateSuccess,
        deleteOuterServerMail,
        clearDeleteOuterServerMail,
        updateFail,
    } = props;
    const classes = useStyles();
    const { shortName } = useParams();
    const intl = useIntl();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);
    const [params, setParams] = useState({});
    const [validateResults, setValidateResults] = useState({});
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [updateMessage, setUpdateMessage] = useState('');
    const [updateSeverity, setUpdateSeverity] = useState('');

    useEffect(() => {
        getOuterServerMail(shortName);

        return () => {
            clearGetOuterServerMail();
            clearUpdateOuterServerMail();
            clearDeleteOuterServerMail();
        };
    }, [
        shortName, getOuterServerMail,
        clearGetOuterServerMail, clearUpdateOuterServerMail,
        clearDeleteOuterServerMail,
    ]);

    useEffect(() => {
        if (!_.isEmpty(mailSetting)) {
            setParams({
                email_title_viewer: mailSetting.email_title_viewer || '',
                email_title_organizer: mailSetting.email_title_organizer || '',
            });
        }
    }, [mailSetting]);

    useEffect(() => {
        if (updateSuccess) {
            setUpdateMessage('更新成功');
            setUpdateSeverity('success');
            setValidateResults({});
            clearUpdateOuterServerMail();
            clearDeleteOuterServerMail();
            getOuterServerMail(shortName);
        }
    }, [clearUpdateOuterServerMail, clearDeleteOuterServerMail, updateSuccess, getOuterServerMail, shortName]);

    useEffect(() => {
        if (!_.isEmpty(updateFail)) {
            setUpdateMessage(<FormattedMessage id="apiErrors.default" />);
            setUpdateSeverity('error');
        }
    }, [updateFail]);

    useEffect(() => {
        if (updateSuccess || !_.isEmpty(updateFail)) {
            setIsOpenSnackBar(true);
        }
    }, [updateSuccess, updateFail]);

    const handleSetParams = useCallback((field, valueChange) => {
        setParams((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);

    const validate = (submitData) => {
        const {
            email_title_viewer,
            email_content_viewer,
            email_title_organizer,
            email_content_organizer,
        } = submitData;
        let validateRules = {
            email_title_viewer: [
                () => Validation.isNotEmpty(email_title_viewer),
                () => Validation.validateMaxLength(email_title_viewer, 255),
            ],
            email_title_organizer: [
                () => Validation.isNotEmpty(email_title_organizer),
                () => Validation.validateMaxLength(email_title_organizer, 255),
            ],
        };

        if (_.isNil(mailSetting?.email_content_viewer)) {
            validateRules = {
                ...validateRules,
                email_content_viewer: [
                    () => Validation.isNotEmpty(email_content_viewer),
                ],
            };
        }

        if (_.isNil(mailSetting?.email_content_organizer)) {
            validateRules = {
                ...validateRules,
                email_content_organizer: [
                    () => Validation.isNotEmpty(email_content_organizer),
                ],
            };
        }

        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);

            return false;
        }
        setValidateResults({});

        return true;
    };

    const handleUpdateMail = () => {
        if (validate(params)) {
            updateOuterServerMail(shortName, params);
        }
    };

    const handleDeleteMail = () => {
        deleteOuterServerMail(shortName);
        setOpenConfirmDialog(false);
    };

    return (
        <>
            <GridContainer style={{ justifyContent: 'center' }}>
                <GridItem md={10} className={classes.main}>
                    <fieldset className={classes.fieldset}>
                        <legend>
                            <Typography variant="h5" color="primary" bold>外部サーバー専用メール自動送信設定</Typography>
                        </legend>
                        <GridContainer className={classes.marginBottom}>
                            <GridItem md={2}>
                                入場者の件名
                            </GridItem>
                            <GridItem md={10}>
                                <CleanableInput
                                    defaultValue={params.email_title_viewer}
                                    onChange={(value) => handleSetParams('email_title_viewer', value)}
                                    error={!!validateResults.email_title_viewer}
                                    helperText={validateResults.email_title_viewer
                                        ? intl.formatMessage({ id: validateResults.email_title_viewer.message }, { maxLength: 255 }) : ''}
                                />
                            </GridItem>
                        </GridContainer>
                        <GridContainer className={classes.marginBottom}>
                            <GridItem md={2}>
                                入場者のメール文言
                            </GridItem>
                            <GridItem md={10}>
                                {mailSetting?.email_content_viewer && (
                                    <a href={mailSetting?.email_content_viewer} target="blank" className={classes.marginBottom}>
                                        {mailSetting?.email_content_viewer}
                                    </a>
                                )}
                                <UploadButton
                                    namePreview={mailSetting?.email_content_viewer && mailSetting?.email_content_viewer.slice(mailSetting?.email_content_viewer.lastIndexOf('/') + 1)}
                                    type="html"
                                    onChange={(value) => handleSetParams('email_content_viewer', value)}
                                />
                                {
                                    !!validateResults.email_content_viewer && (
                                        <Typography
                                            color="danger"
                                            variant="caption"
                                            component="div"
                                            classes={{ caption: classes.errorText }}
                                        >
                                            {intl.formatMessage({ id: validateResults.email_content_viewer.message })}
                                        </Typography>
                                    )
                                }
                            </GridItem>
                        </GridContainer>
                        <GridContainer className={classes.marginBottom}>
                            <GridItem md={2}>
                                出展者の件名
                            </GridItem>
                            <GridItem md={10}>
                                <CleanableInput
                                    defaultValue={params.email_title_organizer}
                                    onChange={(value) => handleSetParams('email_title_organizer', value)}
                                    error={!!validateResults.email_title_organizer}
                                    helperText={validateResults.email_title_organizer
                                        ? intl.formatMessage({ id: validateResults.email_title_organizer.message }, { maxLength: 255 }) : ''}
                                />
                            </GridItem>
                        </GridContainer>
                        <GridContainer className={classes.marginBottom}>
                            <GridItem md={2}>
                                出展者のメール文言
                            </GridItem>
                            <GridItem md={10}>
                                {mailSetting?.email_content_organizer && (
                                    <a href={mailSetting?.email_content_organizer} target="blank" className={classes.marginBottom}>
                                        {mailSetting?.email_content_organizer}
                                    </a>
                                )}
                                <UploadButton
                                    namePreview={mailSetting?.email_content_organizer && mailSetting?.email_content_organizer.slice(mailSetting?.email_content_organizer.lastIndexOf('/') + 1)}
                                    type="html"
                                    onChange={(value) => handleSetParams('email_content_organizer', value)}
                                />
                                {
                                    !!validateResults.email_content_organizer && (
                                        <Typography
                                            color="danger"
                                            variant="caption"
                                            component="div"
                                            classes={{ caption: classes.errorText }}
                                        >
                                            {intl.formatMessage({ id: validateResults.email_content_organizer.message })}
                                        </Typography>
                                    )
                                }
                            </GridItem>
                        </GridContainer>
                    </fieldset>
                </GridItem>
                <GridItem md={10} style={{ textAlign: 'center', marginTop: 20 }}>
                    <Button
                        color="info"
                        style={{ width: '200px', fontSize: '20px', marginRight: '15px' }}
                        size="md"
                        gradient
                        onClick={() => handleUpdateMail()}
                    >
                        保存する
                    </Button>
                    <Button
                        color="danger"
                        style={{ width: '200px', fontSize: '20px' }}
                        size="md"
                        gradient
                        onClick={() => setOpenConfirmDialog(true)}
                        disabled={!mailSetting?.email_title_viewer}
                    >
                        クリア
                    </Button>
                </GridItem>
            </GridContainer>

            <AlertDialog
                title="確認ダイアログ"
                handleClose={() => setOpenConfirmDialog(false)}
                handleOpen={openConfirmDialog}
                handleSubmit={handleDeleteMail}
                contents="システムの固定メールテンプレートに戻ってよろしいでしょうか？"
            />

            <SnackBar
                open={isOpenSnackBar}
                onClose={() => setIsOpenSnackBar(false)}
                message={updateMessage}
                severity={updateSeverity}
                autoHideDuration={3000}
            />

            <Loader isLoading={isLoading} />
        </>
    );
};

OuterServerMail.defaultProps = {
    mailSetting: {},
    updateSuccess: undefined,
    updateFail: {},
};

OuterServerMail.propTypes = {
    getOuterServerMail: PropTypes.func.isRequired,
    clearGetOuterServerMail: PropTypes.func.isRequired,
    updateOuterServerMail: PropTypes.func.isRequired,
    clearUpdateOuterServerMail: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    mailSetting: PropTypes.object,
    updateSuccess: PropTypes.bool,
    deleteOuterServerMail: PropTypes.func.isRequired,
    clearDeleteOuterServerMail: PropTypes.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    updateFail: PropTypes.object,
};

export default OuterServerMail;
